import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import Link from "next/link";

import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import ArrowRightIcon from "@src/lib/icons/18px/arrow-right";
import ChevronLeftIcon from "@src/lib/icons/18px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/18px/chevron-right";
import {
  HomeSectionCountBadge,
  HomeSectionTitle,
} from "@src/features/home/HomeSection";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";
import { LibraryItemType } from "@src/types.generated";
import { formatDateShort } from "@src/features/myTraining/utils";
import { useMyTrainingPageContentTranslations } from "@src/features/myTraining/hooks/useMyTrainingPageContentTranslations";
import {
  useMyTrainingPageMyTodoAssignmentsQuery,
  MyTrainingPageToDoAssignmentFragment,
} from "@src/features/myTraining/MyTrainingPageContent.generated";

const CARD_PAGE_SIZE = 12;

const MyTrainingCarousel: FC = () => {
  const router = useRouter();
  const translations = useMyTrainingPageContentTranslations();

  const { data, loading } = useMyTrainingPageMyTodoAssignmentsQuery({
    variables: {
      input: {},
      pagination: { limit: CARD_PAGE_SIZE, offset: 0 },
    },
    pollInterval: 1000 * 60,
  });

  const assignments = data?.MyTodoAssignments?.objects ?? [];
  const totalCount = data?.MyTodoAssignments?.totalCount ?? 0;
  const hasMore = totalCount > assignments.length;

  const openAssignment = useCallback(
    (item: MyTrainingPageToDoAssignmentFragment) => {
      if (item.currentCourseInstance) {
        router.push({
          pathname: "/my-training/course-instance/[id]",
          query: { id: String(item.currentCourseInstance.id) },
        });
      } else if (item.currentModuleInstance) {
        router.push({
          pathname: "/my-training/module-instance/[id]",
          query: { id: String(item.currentModuleInstance.id) },
        });
      } else if (item.currentSCORMCourseInstance) {
        router.push({
          pathname: "/my-training/scorm-course-instance/[id]",
          query: { id: String(item.currentSCORMCourseInstance.id) },
        });
      }
    },
    [router],
  );

  if (!loading && assignments.length === 0) {
    return null;
  }

  return (
    <HomeSectionWithTrailing
      title="My Training"
      titleHref={{ pathname: "/my-training" }}
      titleMeta={
        totalCount > 0 ? <HomeSectionCountBadge count={totalCount} /> : null
      }
      trailing={
        hasMore ? (
          <Link
            href={{ pathname: "/my-training" }}
            className="text-xs-v4 font-medium-v4 text-muted-foreground hover:text-foreground"
          >
            See all
          </Link>
        ) : null
      }
    >
      {loading && assignments.length === 0 ? (
        <AssignmentSkeletonRow />
      ) : (
        <Carousel>
          {assignments.map((item) => (
            <AssignmentCard
              key={item.id}
              item={item}
              onClick={() => openAssignment(item)}
              typeLabel={getTypeLabel(item.libraryItem.type, translations)}
            />
          ))}
        </Carousel>
      )}
    </HomeSectionWithTrailing>
  );
};

const HomeSectionWithTrailing: FC<{
  title: string;
  titleHref?: ComponentProps<typeof Link>["href"];
  titleMeta?: React.ReactNode;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, titleHref, titleMeta, trailing, children }) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <HomeSectionTitle title={title} titleHref={titleHref} />
          {titleMeta}
        </div>
        {trailing}
      </div>
      {children}
    </div>
  );
};

const Carousel: FC<{ children: React.ReactNode }> = ({ children }) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = useCallback((direction: "left" | "right") => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.8, 320);
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className={cn(
          "hide-scrollbar flex snap-x gap-3 overflow-x-auto scroll-smooth",
          "[scroll-padding-inline:4px]",
        )}
      >
        {children}
      </div>
      {canScrollLeft && (
        <CarouselArrow
          direction="left"
          onClick={() => scrollBy("left")}
          aria-label="Scroll left"
        />
      )}
      {canScrollRight && (
        <CarouselArrow
          direction="right"
          onClick={() => scrollBy("right")}
          aria-label="Scroll right"
        />
      )}
    </div>
  );
};

const CarouselArrow: FC<{
  direction: "left" | "right";
  onClick: () => void;
  "aria-label": string;
}> = ({ direction, onClick, ...ariaProps }) => (
  <Button
    variant="outline"
    size="icon-md"
    onClick={onClick}
    className={cn(
      "absolute top-1/2 z-10 size-8 -translate-y-1/2 rounded-full bg-white shadow-md",
      direction === "left"
        ? "left-0 -translate-x-1/2"
        : "right-0 translate-x-1/2",
    )}
    {...ariaProps}
  >
    {direction === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </Button>
);

const AssignmentCard: FC<{
  item: MyTrainingPageToDoAssignmentFragment;
  typeLabel: string;
  onClick: () => void;
}> = ({ item, typeLabel, onClick }) => {
  const dueDate =
    item.currentCourseInstance?.dueDate ||
    item.currentModuleInstance?.dueDate ||
    item.currentSCORMCourseInstance?.dueDate ||
    null;

  const percentComplete =
    item.currentCourseInstance?.percentComplete ??
    item.currentModuleInstance?.percentComplete ??
    null;
  const displayPercentComplete =
    percentComplete != null
      ? Math.max(0, Math.min(100, Math.round(percentComplete)))
      : null;

  const started = Boolean(
    item.currentCourseInstance?.startedAt ||
      item.currentModuleInstance?.startedAt ||
      item.currentSCORMCourseInstance?.startedAt,
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex w-[260px] shrink-0 snap-start flex-col items-start gap-3 overflow-hidden rounded-2xl border border-gray-2 bg-bg-canvas p-3 text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-4",
      )}
    >
      <div className="flex w-full items-center gap-2">
        <ContentAvatar
          size={40}
          contentType="Module"
          style={{ borderRadius: 10 }}
          coverImage={item.libraryItem.coverImage}
          defaultBackgroundHashKey={item.libraryItem.id}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <Text
            variant="fine-print-description"
            className="text-muted-foreground"
          >
            {typeLabel}
          </Text>
          <Text variant="compact-title" className="truncate text-gray-12">
            {item.libraryItem.name.myTranslation}
          </Text>
        </div>
      </div>

      {displayPercentComplete != null && started && (
        <div className="flex w-full flex-col gap-1">
          <div className="h-0.5 w-full overflow-hidden rounded-full bg-gray-2">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${displayPercentComplete}%` }}
            />
          </div>
          <Text
            variant="fine-print-description"
            className="text-muted-foreground"
          >
            {displayPercentComplete}% complete
          </Text>
        </div>
      )}

      <div className="mt-auto flex w-full items-center justify-between gap-2">
        <Button
          asChild
          variant="outline"
          size="sm"
          className="group-hover:bg-state-hover"
        >
          <span>
            {started ? "Continue" : "Start"}
            <ArrowRightIcon
              data-icon="inline-end"
              className="transition-transform group-hover:translate-x-0.5"
            />
          </span>
        </Button>
        {dueDate && (
          <Text
            variant="fine-print-description"
            className="text-muted-foreground"
          >
            Due {formatDateShort(dueDate)}
          </Text>
        )}
      </div>
    </button>
  );
};

const AssignmentSkeletonRow: FC = () => (
  <div className="flex gap-3 overflow-hidden">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="h-[132px] w-[260px] shrink-0 animate-pulse rounded-2xl border border-gray-2 bg-gray-1"
      />
    ))}
  </div>
);

const getTypeLabel = (
  type: LibraryItemType,
  translations: ReturnType<typeof useMyTrainingPageContentTranslations>,
): string => {
  if (type === LibraryItemType.Module) return translations.module;
  if (type === LibraryItemType.ScormCourse) return translations.scormCourse;
  return translations.course;
};

export default MyTrainingCarousel;
